import { useContext, useEffect, useMemo, useState } from 'react'
import { merchantOptionLabel, sortMerchantOptions } from '../../components/ui/merchantOptions';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserForm, UsersContext } from '../../context/users/UserContext';
import { AccessGroup, AccessLevel, ManagedUser } from '../../models/interfaces';
import Message from '../../components/ui/Message';
import { useRequiredFields } from '../../hooks/useRequiredFields';
import { LEVEL_LABEL, USER_TYPE_LABEL } from '../../utils/access';
import BankOptions from '../../components/ui/BankOptions';
import MerchantPicker from '../../components/ui/MerchantPicker';
import topApi from '../../components/api/ToPApi';
import { formatDay, formatMoney } from '../features/featureUi';

// Comercio con suscripción (retail e inventario por usuario): cuánto aumenta la cuota si se crea un usuario más
interface NewUserCost {
    contracted: number;
    active: number;
    increases: boolean;
    users: number;
    currentAmount: number;
    newAmount: number;
    increment: number;
    currency: string;
    perUserFeatures: string[];
    nextChargeOn: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LEVEL_RANK: Record<AccessLevel, number> = { PLATAFORMA: 3, BANCO: 2, COMERCIO: 1 };

// Nivel del usuario: el más alto de sus grupos (los administradores de plataforma pueden tener comercio asignado)
const levelOfUser = (user: ManagedUser, fallback: AccessLevel): AccessLevel => {
    const levels = user.groups.map(group => group.level).filter((level): level is AccessLevel => !!level && level in LEVEL_RANK);
    if (levels.length) return levels.sort((a, b) => LEVEL_RANK[b] - LEVEL_RANK[a])[0];
    return user.merchantId ? 'COMERCIO' : user.bankId ? 'BANCO' : fallback;
};

/**
 * Crear o editar un usuario: nivel (plataforma, banco o comercio), banco o comercio al que pertenece,
 * tipo (portal u operador del POS) y grupos. Solo se ofrecen los grupos que se pueden asignar.
 */
export const UserPage = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { catalog, suggestions, loadCatalog, saveUser, clearSuggestions } = useContext(UsersContext);
    const required = useRequiredFields();

    const editingUser: ManagedUser | undefined = state?.user;
    const isEdit = !!editingUser;

    const [form, setForm] = useState<UserForm>(() => editingUser
        ? {
            id: editingUser.id,
            username: editingUser.username,
            name: editingUser.name ?? '',
            lastname: editingUser.lastname ?? '',
            email: editingUser.email ?? '',
            level: levelOfUser(editingUser, state?.level ?? 'COMERCIO'),
            bankId: editingUser.bankId ?? null,
            merchantId: editingUser.merchantId ?? null,
            userType: editingUser.userType ?? 'PORTAL',
            groupIds: editingUser.groups.map(group => group.id),
            cashRegister: !!editingUser.cashRegister,
        }
        : {
            username: '', name: '', lastname: '', email: '',
            level: state?.level ?? 'COMERCIO',
            bankId: state?.bankId ?? null,
            merchantId: state?.merchantId ?? null,
            userType: 'PORTAL',
            groupIds: [],
            cashRegister: false,
        });

    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [userCost, setUserCost] = useState<NewUserCost | null>(null);
    const [confirmCost, setConfirmCost] = useState(false);

    useEffect(() => { loadCatalog(); clearSuggestions(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

    // Valores por defecto según lo que permite el catálogo (un solo banco/comercio, solo operadores, etc.)
    useEffect(() => {
        if (!catalog || isEdit) return;
        setForm(prev => {
            const next = { ...prev };
            if (!catalog.levels.includes(next.level)) next.level = catalog.levels[0];
            if (next.level === 'BANCO' && !next.bankId && catalog.banks.length === 1) next.bankId = catalog.banks[0].id;
            if (next.level === 'COMERCIO' && !next.merchantId && catalog.merchants.length === 1) next.merchantId = catalog.merchants[0].id;
            if (!catalog.userTypes.includes(next.userType)) next.userType = catalog.userTypes[0];
            return next;
        });
    }, [catalog, isEdit]);

    const update = <K extends keyof UserForm>(field: K, value: UserForm[K]) => setForm(prev => ({ ...prev, [field]: value }));

    const changeLevel = (level: AccessLevel) => setForm(prev => ({
        ...prev,
        level,
        bankId: level === 'BANCO' && catalog?.banks.length === 1 ? catalog.banks[0].id : null,
        merchantId: level === 'COMERCIO' && catalog?.merchants.length === 1 ? catalog.merchants[0].id : null,
        userType: level === 'COMERCIO' ? prev.userType : 'PORTAL',
        groupIds: [],
    }));

    // Grupos del mismo nivel: plantillas del sistema o grupos del mismo banco/comercio
    const availableGroups = useMemo<AccessGroup[]>(() => (catalog?.groups ?? []).filter(group => {
        if (group.level !== form.level) return false;
        if (group.isSystem) return true;
        if (form.level === 'BANCO') return group.bankId === form.bankId;
        if (form.level === 'COMERCIO') return group.merchantId === form.merchantId;
        return true;
    }), [catalog, form.level, form.bankId, form.merchantId]);

    const toggleGroup = (group: AccessGroup) => {
        if (!group.assignable) return;
        update('groupIds', form.groupIds.includes(group.id)
            ? form.groupIds.filter(id => id !== group.id)
            : [...form.groupIds, group.id]);
    };

    const merchantsForBank = useMemo(() => sortMerchantOptions(catalog?.merchants ?? []), [catalog]);
    const bankName = (id?: number | null) => catalog?.banks.find(bank => bank.id === id)?.name;
    const merchantName = (id?: number | null) => {
        const merchant = catalog?.merchants.find(merchant => merchant.id === id);
        return merchant ? merchantOptionLabel(merchant) : undefined;
    };

    const missing = {
        username: !isEdit && !form.username.trim(),
        name: !form.name.trim(),
        email: !form.email.trim() || !EMAIL_REGEX.test(form.email.trim()),
        bank: form.level === 'BANCO' && !form.bankId,
        merchant: form.level === 'COMERCIO' && !form.merchantId,
        groups: form.groupIds.length === 0,
    };

    // Usuario nuevo de un comercio: si ya usa todos los usuarios contratados, el costo mensual aumenta
    const costMerchantId = !isEdit && form.level === 'COMERCIO' ? form.merchantId : null;
    useEffect(() => {
        setUserCost(null);
        if (!costMerchantId) return;
        let active = true;
        topApi.get<{ cost: NewUserCost | null }>('/merchant-subscription/new-user-cost', { params: { merchantId: costMerchantId } })
            .then(resp => { if (active) setUserCost(resp.data.cost); })
            .catch(() => undefined);   // sin suscripción o sin la tabla: sin aviso
        return () => { active = false; };
    }, [costMerchantId]);
    const costIncreases = !!userCost?.increases;

    const handleSave = async (confirmed = false) => {
        if (Object.values(missing).some(Boolean)) {
            required.fail();
            setErrorMessage(missing.groups && !missing.name && !missing.email ? 'Debe asignar al menos un grupo.' : 'Debe completar los campos requeridos.');
            return;
        }
        // Usuario adicional: se confirma el aumento antes de crearlo
        if (costIncreases && !confirmed) { setConfirmCost(true); return; }
        setConfirmCost(false);
        setSaving(true);
        const resp = await saveUser({ ...form, username: form.username.trim(), email: form.email.trim() });
        setSaving(false);
        if (resp === 'success') {
            setSuccessMessage(isEdit ? 'Usuario actualizado correctamente.'
                : 'Usuario creado correctamente. Se envió la contraseña temporal a su correo.'
                + (costIncreases && userCost ? ` Su cuota mensual ahora es de ${formatMoney(userCost.newAmount, userCost.currency)} (${userCost.users} usuarios).` : ''));
        } else {
            setErrorMessage(resp);
        }
    };

    if (!catalog) {
        return (
            <div className="d-flex justify-content-center mt-5 pt-5">
                <span className="spinner-border text-primary" role="status" />
            </div>
        );
    }

    const canChooseLevel = !isEdit && catalog.levels.length > 1;
    const canChooseBank = !isEdit && form.level === 'BANCO' && catalog.banks.length > 1;
    const canChooseMerchant = !isEdit && form.level === 'COMERCIO' && merchantsForBank.length > 1;
    const canChooseType = !isEdit && form.level === 'COMERCIO' && catalog.userTypes.length > 1;

    return (
        <div className='container mt-5' style={{ maxWidth: 860, flex: 1 }}>

            <div className="d-flex align-items-start gap-3 mb-4">
                <button type="button" className="btn btn-outline-secondary btn-sm mt-1" onClick={() => navigate(-1)} title="Volver">
                    <i className="bi bi-arrow-left" />
                </button>
                <div>
                    <h3 className="mb-1">{isEdit ? 'Editar usuario' : 'Agregar usuario'}</h3>
                    <p className="text-muted mb-0">
                        {LEVEL_LABEL[form.level]}
                        {form.level === 'BANCO' && bankName(form.bankId) && ` · ${bankName(form.bankId)}`}
                        {form.level === 'COMERCIO' && merchantName(form.merchantId) && ` · ${merchantName(form.merchantId)}`}
                    </p>
                </div>
            </div>

            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                    <h6 className="text-muted text-uppercase small mb-3">Pertenece a</h6>
                    <div className="row g-3">
                        {canChooseLevel && (
                            <div className="col-md-4">
                                <label htmlFor="userLevel" className="form-label">Nivel *</label>
                                <select id="userLevel" className="form-select" value={form.level} onChange={(e) => changeLevel(e.target.value as AccessLevel)}>
                                    {catalog.levels.map(level => <option key={level} value={level}>{LEVEL_LABEL[level]}</option>)}
                                </select>
                            </div>
                        )}
                        {canChooseBank && (
                            <div className="col-md-8">
                                <label htmlFor="userBank" className="form-label">Banco *</label>
                                <select id="userBank" className={`form-select${required.mark(missing.bank)}`} value={form.bankId ?? ''}
                                    onChange={(e) => setForm(prev => ({ ...prev, bankId: Number(e.target.value) || null, groupIds: [] }))}>
                                    <option value="">Seleccione el banco</option>
                                    <BankOptions banks={catalog.banks} />
                                </select>
                                <div className="invalid-feedback">Campo requerido</div>
                            </div>
                        )}
                        {canChooseMerchant && (
                            <div className="col-md-8">
                                <label htmlFor="userMerchant" className="form-label">Comercio *</label>
                                <MerchantPicker id="userMerchant" value={form.merchantId ?? -1} allowAll={false}
                                    allLabel="Seleccione el comercio" params={{ activeOnly: '1' }}
                                    selectedLabel={merchantName(form.merchantId) ?? ''} invalid={required.mark(missing.merchant) !== ''}
                                    onChange={(id) => setForm(prev => ({ ...prev, merchantId: id > 0 ? id : null, groupIds: [] }))} />
                                {required.mark(missing.merchant) !== '' && <div className="invalid-feedback d-block">Campo requerido</div>}
                            </div>
                        )}
                        <div className="col-md-4">
                            <label htmlFor="userType" className="form-label">Tipo de usuario</label>
                            {canChooseType ? (
                                <select id="userType" className="form-select" value={form.userType} onChange={(e) => update('userType', e.target.value as UserForm['userType'])}>
                                    {catalog.userTypes.map(type => <option key={type} value={type}>{USER_TYPE_LABEL[type]}</option>)}
                                </select>
                            ) : (
                                <input id="userType" className="form-control" value={USER_TYPE_LABEL[form.userType]} disabled />
                            )}
                            {form.userType === 'OPERADOR' && <div className="form-text">Usa el POS. Se crea también en ToP.</div>}
                        </div>
                    </div>
                </div>
            </div>

            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                    <h6 className="text-muted text-uppercase small mb-3">Datos del usuario</h6>
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label htmlFor="userNameInput" className="form-label">Usuario *</label>
                            <input id="userNameInput" className={`form-control${required.mark(missing.username)}`} disabled={isEdit} autoFocus={!isEdit}
                                value={form.username} onChange={(e) => { update('username', e.target.value); clearSuggestions(); }} />
                            <div className="invalid-feedback">Campo requerido</div>
                            {suggestions.length > 0 && (
                                <div className="mt-2">
                                    <div className="small text-danger mb-1">Ese usuario ya existe. Sugerencias:</div>
                                    {suggestions.map(suggestion => (
                                        <button key={suggestion} type="button" className="btn btn-sm btn-outline-secondary rounded-pill me-1 mb-1"
                                            onClick={() => { update('username', suggestion); clearSuggestions(); }}>
                                            {suggestion}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="col-md-6">
                            <label htmlFor="emailInput" className="form-label">Correo *</label>
                            <input id="emailInput" type="email" className={`form-control${required.mark(missing.email)}`}
                                value={form.email} onChange={(e) => update('email', e.target.value)} />
                            <div className="invalid-feedback">{form.email ? 'Ingrese un correo válido' : 'Campo requerido'}</div>
                        </div>
                        <div className="col-md-6">
                            <label htmlFor="nameInput" className="form-label">Nombre *</label>
                            <input id="nameInput" className={`form-control${required.mark(missing.name)}`}
                                value={form.name} onChange={(e) => update('name', e.target.value)} />
                            <div className="invalid-feedback">Campo requerido</div>
                        </div>
                        <div className="col-md-6">
                            <label htmlFor="lastnameInput" className="form-label">Apellidos</label>
                            <input id="lastnameInput" className="form-control" value={form.lastname} onChange={(e) => update('lastname', e.target.value)} />
                        </div>
                        {form.level === 'COMERCIO' && (
                            <div className="col-12">
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" role="switch" id="cashRegister"
                                        checked={form.cashRegister} onChange={(e) => update('cashRegister', e.target.checked)} />
                                    <label className="form-check-label" htmlFor="cashRegister">Permiso de apertura y cierre de caja</label>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {costIncreases && userCost && (
                <div className="alert alert-warning d-flex gap-3 align-items-start" role="alert">
                    <i className="bi bi-exclamation-triangle fs-4" />
                    <div>
                        <div className="fw-semibold">Este usuario aumenta el costo mensual</div>
                        <div className="small">
                            El plan incluye {userCost.contracted} {userCost.contracted === 1 ? 'usuario' : 'usuarios'} y {userCost.active === 1 ? 'ya está en uso' : `ya hay ${userCost.active} activos`}.
                            {userCost.perUserFeatures.length > 0 && <> {userCost.perUserFeatures.join(', ')} se cobran por usuario:</>} la cuota pasa
                            de <strong>{formatMoney(userCost.currentAmount, userCost.currency)}</strong> a <strong>{formatMoney(userCost.newAmount, userCost.currency)}</strong> al
                            mes (+{formatMoney(userCost.increment, userCost.currency)}), desde el próximo cobro ({formatDay(userCost.nextChargeOn)}).
                        </div>
                    </div>
                </div>
            )}

            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="text-muted text-uppercase small mb-0">Grupos *</h6>
                        <span className="small text-muted">Sus permisos son la suma de los grupos</span>
                    </div>
                    {(form.level === 'BANCO' && !form.bankId) || (form.level === 'COMERCIO' && !form.merchantId) ? (
                        <div className="text-muted small">Seleccione primero {form.level === 'BANCO' ? 'el banco' : 'el comercio'}.</div>
                    ) : availableGroups.length === 0 ? (
                        <div className="text-muted small">No hay grupos para este nivel. Créelos en "Grupos y permisos".</div>
                    ) : (
                        <div className={`row g-2 ${required.attempted && missing.groups ? 'border border-danger rounded p-2' : ''}`}>
                            {availableGroups.map(group => (
                                <div className="col-md-6" key={group.id}>
                                    <label className={`d-flex gap-2 border rounded-3 p-2 h-100 ${group.assignable ? '' : 'bg-light text-muted'}`}
                                        style={{ cursor: group.assignable ? 'pointer' : 'not-allowed' }}
                                        title={group.assignable ? '' : 'Tiene permisos que usted no puede otorgar'}>
                                        <input type="checkbox" className="form-check-input flex-shrink-0 mt-1"
                                            checked={form.groupIds.includes(group.id)} disabled={!group.assignable}
                                            onChange={() => toggleGroup(group)} />
                                        <span>
                                            <span className="fw-medium d-block">
                                                {group.name}
                                                {group.isSystem && <span className="badge bg-secondary-subtle text-secondary-emphasis ms-2">Sistema</span>}
                                                {!group.assignable && <i className="bi bi-lock ms-1 small" />}
                                            </span>
                                            {group.description && <span className="small text-muted">{group.description}</span>}
                                        </span>
                                    </label>
                                </div>
                            ))}
                        </div>
                    )}
                    {required.attempted && missing.groups && <div className="small text-danger mt-2">Asigne al menos un grupo.</div>}
                </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mb-5">
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)} disabled={saving}>Cancelar</button>
                <button type="button" className="btn btn-primary" onClick={() => handleSave()} disabled={saving}>
                    {saving ? <><span className="spinner-border spinner-border-sm me-1" /> Guardando...</> : <><i className="bi bi-check-lg me-1" /> Guardar</>}
                </button>
            </div>

            {confirmCost && userCost && (
                <Message type='success' title='Usuario adicional'
                    message={`Al crear este usuario la cuota mensual aumenta de ${formatMoney(userCost.currentAmount, userCost.currency)} a ${formatMoney(userCost.newAmount, userCost.currency)} (${userCost.users} usuarios) desde el próximo cobro. ¿Desea continuar?`}
                    showCancel={true} okText='Continuar' cancelText='Cancelar' handleCancel={() => setConfirmCost(false)} closeDialog={() => setConfirmCost(false)}
                    handleOk={() => handleSave(true)} backgroundColor='rgba(0,0,0,0.4)' />
            )}
            {errorMessage && (
                <Message type='error' message={errorMessage} closeDialog={() => setErrorMessage('')} handleOk={() => setErrorMessage('')} backgroundColor='rgba(0,0,0,0.4)' />
            )}
            {successMessage && (
                <Message type='success' message={successMessage} title='Exitoso'
                    closeDialog={() => navigate(-1)} handleOk={() => navigate(-1)} backgroundColor='rgba(0,0,0,0.4)' />
            )}
        </div>
    )
}
